import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import estimateProvider from "@/providers/estimate.provider";
import { ESTIMATE_KEYS } from "@/hooks/queries/estimates/queryKeys";
import type { GetEstimateDetailData } from "@/hooks/queries/estimates/useGetEstimateDetailQuery";
import { prepareBatchOperations, type BatchOperation } from "@/lib/estimate-line-items";

export interface BatchUpdateLineItemsMutationParams {
  estimateId: string;
  operations: BatchOperation[];
}

export function useBatchUpdateLineItemsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: BatchUpdateLineItemsMutationParams) => {
      const currentData = await queryClient.ensureQueryData<GetEstimateDetailData>({
        queryKey: ESTIMATE_KEYS.detail(params.estimateId),
        queryFn: async () => {
          const { data, success, exportedToXactimate, files, organizationId } = await estimateProvider.getEstimateDetail(params.estimateId);
          if (!success) {
            throw new Error("Failed to fetch estimation detail");
          }
          return { estimationDetail: data ?? [], exportedToXactimate, files: files ?? [], organizationId };
        },
      });

      if (!currentData?.estimationDetail) {
        throw new Error("No estimation detail found");
      }

      const { batchApiPayload, modifiedState } =
        prepareBatchOperations(currentData.estimationDetail, params.operations, params.estimateId) || {};

      if (!batchApiPayload || !modifiedState) {
        throw new Error("Failed to prepare batch operations");
      }

      const { success, failedOperations, totalOperations } = await estimateProvider.batchUpdateOrCreateLineItems({
        operations: batchApiPayload,
      });

      if (!success || failedOperations > 0) {
        throw new Error(`${failedOperations} out of ${totalOperations} operations failed`);
      }

      return { success, failedOperations, totalOperations, modifiedState };
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: ESTIMATE_KEYS.detail(variables.estimateId),
      });

      const previousData = queryClient.getQueryData<GetEstimateDetailData>(ESTIMATE_KEYS.detail(variables.estimateId));

      if (previousData?.estimationDetail) {
        const result = prepareBatchOperations(previousData.estimationDetail, variables.operations, variables.estimateId);

        if (result) {
          queryClient.setQueryData(ESTIMATE_KEYS.detail(variables.estimateId), {
            ...previousData,
            estimationDetail: result.modifiedState,
          });
        }
      }

      return { previousData };
    },
    onError: (error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(ESTIMATE_KEYS.detail(variables.estimateId), context.previousData);
      }
      toast.error("Failed to update line items");
      console.error("Batch update line items error:", error);
    },
    onSuccess: ({ modifiedState }, variables) => {
      queryClient.setQueryData<GetEstimateDetailData>(ESTIMATE_KEYS.detail(variables.estimateId), (old) => {
        if (!old) return old;
        return { ...old, estimationDetail: modifiedState };
      });
    },
  });
}
